import { makeAutoObservable, runInAction } from 'mobx'
import { RootStore } from 'stores'
import {
  getMyNotifications,
  seenAllNotifications,
  countNotifications,
  getNotificationCountsForProfiles,
  seenNotification
} from 'API/notification'
import { acceptInvitation, declineInvitation } from 'API/invitation'
import { INotificationWithRelations } from 'interfaces/notification'
import { getApiErrorDetails } from 'utils/authError'
import { toast } from 'react-toastify'
import { INVITATION_ABORT_CODES } from 'constants/auth-errors'

const PAGE_SIZE = 10

/** Per-notification mutable state tracked in the store for double-click guard. */
type NotificationMutatingState = 'accept' | 'deny' | null

class NotificationStore {
  rootStore: RootStore

  isFetching: boolean = false

  /**
   * Legacy: account-wide unseen count fetched from BE.
   * Still used by LeftSidebar, NotificationPage, hooks, etc.
   * After Phase 7 consumers migrate to `notificationCountForActiveProfile`, this can be removed.
   */
  countNotifications: number = 0

  notifications: INotificationWithRelations[] = []

  /** Profile-keyed unread counts for SelectProfilePage badges. Key = `${type}-${id}`. */
  countsByProfile: Map<string, number> = new Map()

  /** Last profile key whose notifications were loaded — used for staleness detection. */
  lastFetchedProfileId: string | null = null

  /** Per-notification mutating state to prevent double-submit. */
  private _mutatingNotifs: Map<number, NotificationMutatingState> = new Map()

  /**
   * Rapid-switch mutex token. Each call to `syncAfterProfileSwitch` stamps a new
   * Symbol; if the token changes between awaits, the earlier call is stale and
   * must early-return so a faster switch's results are not overwritten.
   */
  private _switchToken: symbol | null = null

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  // ---------------------------------------------------------------------------
  // Computed
  // ---------------------------------------------------------------------------

  /**
   * Profile-scoped unread count derived from the current notification list.
   * Reactive: components observing this re-render when any `isSeen` flips.
   *
   * Previously: aggregateCountNotifications() fetched an account-wide count.
   * Now: computed directly from the active-profile notification list.
   */
  get notificationCountForActiveProfile(): number {
    return this.notifications.filter(n => !n.isSeen).length
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  getMutatingState(notificationId: number): NotificationMutatingState {
    return this._mutatingNotifs.get(notificationId) ?? null
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Fetch the current active-profile's notification list.
   * Includes mutex-token guard: if a newer profile switch starts mid-fetch, this
   * call detects the stale token and discards its result.
   */
  public async fetchNotifications(fetchNextPage = false): Promise<void> {
    const tokenAtStart = this._switchToken
    this.isFetching = true
    try {
      const skip = fetchNextPage ? this.notifications.length : 0
      const page = await getMyNotifications(PAGE_SIZE, skip)
      // Token check: if a newer switch fired between the await above and now, discard.
      if (this._switchToken !== tokenAtStart) return
      runInAction(() => {
        if (fetchNextPage) {
          this.notifications = [...this.notifications, ...page]
        } else {
          this.notifications = page
        }
      })
    } finally {
      runInAction(() => { this.isFetching = false })
    }
  }

  /**
   * Fetch the account-wide unseen count from BE (legacy path used by LeftSidebar, hooks,
   * NotificationPage, etc.). Kept for backward compatibility; Phase 7 will migrate
   * those consumers to `notificationCountForActiveProfile`.
   */
  public async aggregateCountNotifications(): Promise<void> {
    this.countNotifications = await countNotifications()
  }

  public async seenAllNotifications(): Promise<void> {
    await seenAllNotifications()
    runInAction(() => {
      this.countNotifications = 0
      this.notifications = this.notifications.map(n => ({ ...n, isSeen: true }))
    })
  }

  /**
   * Batch-fetch unread counts for multiple profiles at once.
   * Populates `countsByProfile` so SelectProfilePage can show per-profile badges
   * without fetching each profile's full notification list.
   *
   * @param profileKeys - array of `${type}-${id}` strings
   */
  public async loadCountsForAllProfiles(profileKeys: string[]): Promise<void> {
    if (profileKeys.length === 0) return
    try {
      const counts = await getNotificationCountsForProfiles(profileKeys)
      runInAction(() => {
        for (const [key, count] of Object.entries(counts)) {
          this.countsByProfile.set(key, count)
        }
      })
    } catch (err) {
      console.warn('[notificationStore] loadCountsForAllProfiles failed', err)
    }
  }

  /**
   * Returns the cached per-profile count for a given profile key.
   * Reactive: re-reads from `countsByProfile` observable map.
   */
  public notificationCountForProfile(profileKey: string): number {
    return this.countsByProfile.get(profileKey) ?? 0
  }

  /**
   * Called by authStore.switchTo*Profile after memberships refresh.
   * Reloads the notification list scoped to the newly-active profile.
   *
   * (No BE "sync" call: invite notifications are already created at invite-time
   * for existing profiles and backfilled at profile-creation / registration, so
   * the old per-switch /profiles/sync-notifications backfill was a redundant no-op.)
   *
   * Mutex-token guard prevents an older switch from overwriting a newer switch's list
   * (rapid double-switch race condition).
   */
  public async syncAfterProfileSwitch(profileKey: string): Promise<void> {
    const token = Symbol(profileKey)
    this._switchToken = token
    // Show the bell spinner for the whole switch window and drop the previous
    // profile's list so it can't flash stale while the new profile's
    // notifications load. fetchNotifications() clears isFetching when it resolves.
    runInAction(() => {
      this.isFetching = true
      this.notifications = []
    })
    try {
      await this.fetchNotifications()
      if (this._switchToken !== token) return // stale: newer switch superseded us
      runInAction(() => {
        this.lastFetchedProfileId = profileKey
      })
    } catch (err) {
      console.warn('[notificationStore] syncAfterProfileSwitch failed', err)
      // Clear the spinner if fetchNotifications threw. Guard against a newer
      // switch owning the flag.
      if (this._switchToken === token) {
        runInAction(() => {
          this.isFetching = false
        })
      }
    }
  }

  /**
   * Accept an org invitation from the notification bell.
   * Keeps the notification visible with status="accepted" so the renderer
   * shows the "Joined" pill (per cron-cascade UX in plan §6 — terminal-state
   * invitations stay in the list, only badge count decrements). Refreshes
   * memberships so OrgSwitcher picks up the new team.
   */
  public async acceptInvitation(notificationId: number, invitationToken: string): Promise<void> {
    if (this._mutatingNotifs.get(notificationId)) return
    runInAction(() => { this._mutatingNotifs.set(notificationId, 'accept') })
    try {
      await acceptInvitation(invitationToken)
      await seenNotification(notificationId)
      runInAction(() => {
        this.notifications = this.notifications.map(n =>
          n.id === notificationId
            ? { ...n, isSeen: true, invitation: n.invitation ? { ...n.invitation, status: 'accepted' } : n.invitation }
            : n
        )
        this._decrementActiveProfileCount()
      })
      await this.rootStore.authStore.fetchMyMemberships()
    } catch (err: unknown) {
      const apiErr = err as {
        response?: { status?: number; data?: { error?: { code?: string; message?: string } } }
        message?: string
      }
      const { code, message } = getApiErrorDetails(apiErr)
      // Post-2.69.0 the account-uniqueness 409 no longer exists; only terminal-
      // state invitation aborts (expired / revoked / email mismatch) get bespoke
      // copy. Anything else falls through to the generic error toast.
      if (code && INVITATION_ABORT_CODES.includes(code)) {
        toast.error(message ?? 'This invitation is invalid or expired.')
        await this.fetchNotifications()
      } else {
        toast.error('Could not accept invitation. Please try again.')
      }
    } finally {
      runInAction(() => { this._mutatingNotifs.delete(notificationId) })
    }
  }

  /**
   * Decline an org invitation from the notification bell.
   * Keeps the notification visible with status="declined" so the renderer
   * shows the "Declined" pill. Decrements unread count via isSeen=true.
   */
  public async denyInvitation(notificationId: number, invitationToken: string): Promise<void> {
    if (this._mutatingNotifs.get(notificationId)) return
    runInAction(() => { this._mutatingNotifs.set(notificationId, 'deny') })
    try {
      await declineInvitation(invitationToken)
      await seenNotification(notificationId)
      runInAction(() => {
        this.notifications = this.notifications.map(n =>
          n.id === notificationId
            ? { ...n, isSeen: true, invitation: n.invitation ? { ...n.invitation, status: 'declined' } : n.invitation }
            : n
        )
        this._decrementActiveProfileCount()
      })
    } catch (err: unknown) {
      const apiErr = err as { response?: { status?: number; data?: { error?: { message?: string } } }; message?: string }
      const { message } = getApiErrorDetails(apiErr)
      toast.error(message ?? 'Could not decline invitation. Please try again.')
    } finally {
      runInAction(() => { this._mutatingNotifs.delete(notificationId) })
    }
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private _decrementActiveProfileCount(): void {
    if (!this.lastFetchedProfileId) return
    const current = this.countsByProfile.get(this.lastFetchedProfileId) ?? 0
    this.countsByProfile.set(this.lastFetchedProfileId, Math.max(0, current - 1))
  }
}

export default NotificationStore
